import { Table, Text } from "@oysterhr/ui"
import classNames from "classnames"
import { upperCase } from "lodash-es"
import { memo, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { SimpleMoney } from "@oysterhr/common"
import { titleize } from "~/app/utils/stringHelpers"
import { BenefitsPlan } from "@oysterhr/schema"
import { getBenefitsPlanName } from "../../utils/benefits"

export interface BenefitsPlanRowProps {
  benefitPlan: BenefitsPlan
  onPriceClick: (plan: BenefitsPlan) => void
}

export const BenefitsPlanRow = memo(({ benefitPlan, onPriceClick }: BenefitsPlanRowProps) => {
  const [t] = useTranslation("benefits/CoverageType")
  const formattedCurrencyList = useMemo(
    () => benefitPlan.currencies.map(({ code }) => code).join(", "),
    [benefitPlan.currencies],
  )
  const formattedCountryList = useMemo(
    () => benefitPlan.countries.join(", "),
    [benefitPlan.countries],
  )
  const defaultPrice = benefitPlan.prices.find(price => price.isDefault)
  const defaultCurrency =
    defaultPrice?.currencies.find(currency => currency.currencyCode === "USD") ||
    defaultPrice?.currencies[0]

  const hasPrices = benefitPlan.prices.length > 0

  return (
    <Table.Row
      key={benefitPlan.id}
      data-testid={benefitPlan.id}
      className="items-center border-b border-gray-200 hover:bg-gray-200"
    >
      <Table.Cell className="min-w-51 pr-6">
        <Text>{getBenefitsPlanName(benefitPlan)}</Text>
        <Text size="tiny" className="text-gray-300">
          {upperCase(benefitPlan.provider.name)}
        </Text>
      </Table.Cell>
      <Table.Cell className="min-w-32 pr-6">{titleize(benefitPlan.category)}</Table.Cell>
      <Table.Cell className="min-w-32 pr-6">{formattedCurrencyList}</Table.Cell>
      <Table.Cell className="min-w-32 max-w-56 pr-6 text-xs">{formattedCountryList}</Table.Cell>
      <Table.Cell className="whitespace-pre pr-6">
        <Text
          data-testid={`price-${benefitPlan.id}`}
          className={classNames("min-w-16 text-left", {
            underline: hasPrices,
            "hover:no-underline": hasPrices,
            "hover:cursor-pointer": hasPrices,
          })}
          onClick={() => onPriceClick(benefitPlan)}
        >
          {defaultCurrency ? SimpleMoney.from(defaultCurrency).format() : "N/A"}
        </Text>
      </Table.Cell>
      <Table.Cell className="min-w-52">
        {benefitPlan.coverageTypes.map(type => t(type)).join(", ")}
      </Table.Cell>
    </Table.Row>
  )
})
